import * as React from 'react';
import ApiInfoBubble from '../../info/api-info-bubble';
import StatChart from '../../info/stat-chart';
import { getEsHs, percentLabel } from "../../../utility/api-utilities";
import HorizontalStatChart from '../../info/horizontal-stat-chart';
import ApiTooltip from '../../info/api-info-bubble';

// return epi version of a provided filename (all lower case and dashes for spaces)
function epiFile(file) {
    return (file.replace(/\s+/g, '-').toLowerCase());
}

function ReportsTab(props) {
    const school = props.school;
    const rep = school._progressReport;
    const imgCreativeSchoolCertification = epiFile(`https://www.cps.edu/globalassets/cps-global-media/school-profiles/images/arts-${rep.creative_School_Certification ? rep.creative_School_Certification : 'NotYetAvailable'}.png`);
    const imgSupportiveSchoolAward = epiFile(`https://www.cps.edu/globalassets/cps-global-media/school-profiles/images/SEL_${rep.supportive_School_Award ? rep.supportive_School_Award : 'default'}.png`);

    // these checks are different than normal because we don't have epi facets here
    const hs = school.primaryCategory == 'HS';
    const es = !hs; // just to make the component more readable

    const behaviorData = [
        {
            label: 'Number of out-of-school suspensions per 100 students',
            data: [
                rep.suspensions_Per_100_Students_Year_1_Pct,
                rep.suspensions_Per_100_Students_Year_2_Pct,
                rep.suspensions_Per_100_Students_Avg_Pct
            ]
        },
        {
            label: 'Percent of misconducts resulting in out-of-school suspension',
            data: [
                percentLabel(rep.misconducts_To_Suspensions_Year_1_Pct),
                percentLabel(rep.misconducts_To_Suspensions_Year_2_Pct),
                percentLabel(rep.misconducts_To_Suspensions_Avg_Pct),
            ]
        },
        {
            label: 'Average length of out-of-school suspensions',
            data: [
                rep.average_Length_Suspension_Year_1_Pct,
                rep.average_Length_Suspension_Year_2_Pct,
                rep.average_Length_Suspension_Avg_Pct
            ]
        }
    ];

    const otherData = [
        {
            label: 'Student attendance',
            data: [
                percentLabel(rep.student_Attendance_Year_1_Pct),
                percentLabel(rep.student_Attendance_Year_2_Pct),
                percentLabel(rep.student_Attendance_Avg_Pct)
            ]
        },
        {
            label: 'Teacher attendance',
            data: [
                percentLabel(rep.teacher_Attendance_Year_1_Pct),
                percentLabel(rep.teacher_Attendance_Year_2_Pct),
                percentLabel(rep.teacher_Attendance_Avg_Pct)
            ]
        },
    ];
    if (hs) {
        otherData.push({
            label: '1 Year Dropout Rate',
            data: [
                percentLabel(rep.one_Year_Dropout_Rate_Year_1_Pct),
                percentLabel(rep.one_Year_Dropout_Rate_Year_2_Pct),
                percentLabel(rep.one_Year_Dropout_Rate_Avg_Pct)
            ]
        })
    };

    const surveyData = [
        {
            label: 'Students',
            data: [
                percentLabel(rep.school_Survey_Student_Response_Rate_Pct),
                percentLabel(rep.school_Survey_Student_Response_Rate_Avg_Pct)
            ]
        },
        {
            label: 'Teachers',
            data: [
                percentLabel(rep.school_Survey_Teacher_Response_Rate_Pct),
                percentLabel(rep.school_Survey_Teacher_Response_Rate_Avg_Pct)
            ]
        },
        {
            label: 'Parents',
            data: [
                percentLabel(rep.school_Survey_Parent_Response_Rate_Pct),
                percentLabel(rep.school_Survey_Parent_Response_Rate_Avg_Pct)
            ]
        },
    ];

    const nweaReading = [
        {
            title: `3rd`,
            label: percentLabel(rep.nweA_Reading_Growth_Grade_3_Lbl),
            percent: rep.newA_Reading_Growth_Grade_3_Pct,
        },
        ...([4, 5, 6, 7, 8].map(g => ({
            title: `${g}th`,
            label: percentLabel(rep[`nweA_Reading_Growth_Grade_${g}_Lbl`]),
            percent: rep[`nweA_Reading_Growth_Grade_${g}_Pct`],
        }))),
        {
            title: `All`,
            label: percentLabel(rep.growth_Reading_Grades_Tested_Label_ES),
            percent: rep.growth_Reading_Grades_Tested_Pct_ES,
        },
    ];

    const nweaMath = [
        {
            title: `3rd`,
            label: percentLabel(rep.nweA_Math_Growth_Grade_3_Lbl),
            percent: rep.newA_Math_Growth_Grade_3_Pct,
        },
        ...([4, 5, 6, 7, 8].map(g => ({
            title: `${g}th`,
            label: percentLabel(rep[`nweA_Math_Growth_Grade_${g}_Lbl`]),
            percent: rep[`nweA_Math_Growth_Grade_${g}_Pct`],
        }))),
        {
            title: `All`,
            label: percentLabel(rep.growth_Math_Grades_Tested_Label_ES),
            percent: rep.growth_Math_Grades_Tested_Pct_ES,
        },
    ];

    const attainmentReading = [
        {
            title: `3rd`,
            label: percentLabel(rep.nweA_Reading_Attainment_Grade_3_Lbl),
            percent: rep.newA_Reading_Attainment_Grade_3_Pct,
        },
        ...([4, 5, 6, 7, 8].map(g => ({
            title: `${g}th`,
            label: percentLabel(rep[`nweA_Reading_Attainment_Grade_${g}_Lbl`]),
            percent: rep[`nweA_Reading_Attainment_Grade_${g}_Pct`],
        }))),
    ];

    const hsGrowth = [
        {
            title: '9th Grade (PSAT 9) Cohort',
            label: percentLabel(rep[`growth_PSAT_Grade_9_School_Lbl`]),
            percent: rep['growth_PSAT_Grade_9_School_Pct']
        },
        {
            title: '10th Grade (PSAT 10) Reading',
            label: percentLabel(rep[`growth_PSAT_Reading_Grade_10_School_Lbl`]),
            percent: rep['growth_PSAT_Reading_Grade_10_School_Pct']
        },
        {
            title: '10th Grade (PSAT 10) Math',
            label: percentLabel(rep[`growth_PSAT_Math_Grade_10_School_Lbl`]),
            percent: rep['growth_PSAT_Math_Grade_10_School_Pct']
        },
        {
            title: '11th Grade (SAT) Reading',
            label: percentLabel(rep[`growth_SAT_Reading_Grade_11_School_Lbl`]),
            percent: rep['growth_SAT_Reading_Grade_11_School_Pct']
        },
        {
            title: '11th Grade (SAT) Math',
            label: percentLabel(rep[`growth_SAT_Math_Grade_11_School_Lbl`]),
            percent: rep['growth_SAT_Math_Grade_11_School_Pct']
        },
        {
            title: '11th Grade (SAT) Cohort',
            label: percentLabel(rep[`growth_SAT_Grade_11_School_Lbl`]),
            percent: rep['growth_SAT_Grade_11_School_Pct']
        },
    ];

    const hsAttainment = [
        {
            title: '9th Grade (PSAT 9)',
            label: percentLabel(rep[`attainment_PSAT_Grade_9_School_Lbl`]),
            percent: rep['attainment_PSAT_Grade_9_School_Pct']
        },
        {
            title: '10th Grade (PSAT 10)',
            label: percentLabel(rep[`attainment_PSAT_Grade_10_School_Lbl`]),
            percent: rep['attainment_PSAT_Grade_10_School_Pct']
        },
        {
            title: '11th Grade (PSAT 11)',
            label: percentLabel(rep[`attainment_SAT_Grade_11_School_Lbl`]),
            percent: rep['attainment_SAT_Grade_11_School_Pct']
        },
        {
            title: 'All',
            label: percentLabel(rep[`attainment_All_Grades_School_Lbl`]),
            percent: rep['attainment_All_Grades_School_Pct']
        },
    ];

    const attainmentMath = [
        {
            title: `3rd`,
            label: percentLabel(rep.nweA_Math_Attainment_Grade_3_Lbl),
            percent: rep.newA_Math_Attainment_Grade_3_Pct,
        },
        ...([4, 5, 6, 7, 8].map(g => ({
            title: `${g}th`,
            label: percentLabel(rep[`nweA_Math_Attainment_Grade_${g}_Lbl`]),
            percent: rep[`nweA_Math_Attainment_Grade_${g}_Pct`],
        }))),
    ];

    const body = (data) => data.map(line => <tr key={line.label}>
        <td>{line.label}</td>
        {line.data.map((cell, i) => <td key={`cell${i}`}>{!isNaN(parseInt(cell)) ? cell : '---'}</td>)}
    </tr>)

    return <div className="profile-section container-fluid report-tab">
        <div className="row basics report-block">
            <div className="col">
                <div className="basics-columns">
                    <div className="basics-block">
                        <div className="header">
                            <div className="title">Culture & Climate <ApiInfoBubble index="profile.reports.cultureClimate" placement="top" /></div>
                            <div className="value">{rep.culture_Climate_Rating}</div>
                        </div>
                        <p dangerouslySetInnerHTML={{ __html: rep.culture_Climate_Description }}></p>
                    </div>
                    <div className="basics-block">
                        <div className="header">
                            <div className="title">Student Growth <ApiInfoBubble index="profile.reports.schoolRating" placement="top" /></div>
                            <div className="value">{rep.student_Growth_Rating}</div>
                        </div>
                        <p dangerouslySetInnerHTML={{ __html: rep.student_Growth_Description }}></p>
                    </div>
                    <div className="basics-block">
                        <div className="header">
                            <div className="title">Student Attainment <ApiInfoBubble index="profile.reports.studentGrowth" placement="top" /></div>
                            <div className="value">{rep.student_Attainment_Rating}</div>
                        </div>
                        <p dangerouslySetInnerHTML={{ __html: rep.student_Attainment_Description }}></p>
                    </div>
                    <div className="basics-block">
                        <div className="header">
                            <div className="title">Accountability Status
                                {/*<ApiInfoBubble index="profile.reports.schoolRating" placement="top" />*/}
                            </div>
                            <div className="value">{school.overallRatingStatus}</div>
                        </div>
                        <p>Per Chicago Board of Education policy, this school’s accountability status is carrying over from SY 2020-2021.</p>
                        {/*<p dangerouslySetInnerHTML={{ __html: school.overallRatingDescription }}></p>*/}
                    </div>
                </div>
            </div>
        </div>
        {es &&
            <HorizontalStatChart stats={[
                {
                    title: `Reading Growth`,
                    label: `${rep.growth_Reading_Grades_Tested_Label_ES}`,
                    percent: rep.growth_Reading_Grades_Tested_Pct_ES,
                    averageLabel: `National Average 50th Percentile`,
                    averagePercent: 50
                },
                {
                    title: `Math Growth`,
                    label: `${rep.growth_Math_Grades_Tested_Label_ES}`,
                    percent: rep.growth_Math_Grades_Tested_Pct_ES,
                    averageLabel: `National Average 50th Percentile`,
                    averagePercent: 50
                },
            ]} />
        }

        {hs &&
            <HorizontalStatChart stats={[
                {
                    title: <>SAT Average <ApiTooltip index="profile.reports.satAverage" /></>,
                    label: `${rep.saT_Grade_11_Score_School_Avg}`,
                    percent: rep.saT_Grade_11_Score_School_Avg != 0 ? (rep.saT_Grade_11_Score_School_Avg / 1600) * 100 : 0,
                    averageLabel: 'CPS Average',
                    averagePercent: 50
                },
                {
                    title: <>5-Year Cohort Graduation Rate <ApiTooltip index="profile.reports.cohortGraduationRate" /></>,
                    label: `${percentLabel(rep.graduation_5_Year_School_Pct_Year_2)}`,
                    percent: rep.graduation_5_Year_School_Pct_Year_2,
                    averageLabel: `CPS Average (${rep.graduation_5_Year_CPS_Pct_Year_2})`,
                    averagePercent: rep.graduation_5_Year_CPS_Pct_Year_2
                },
                {
                    title: <>College Enrollment <ApiTooltip index="profile.reports.collegeEnrollment" /></>,
                    label: `${percentLabel(rep.college_Enrollment_School_Pct_Year_2)}`,
                    percent: rep.college_Enrollment_School_Pct_Year_2,
                    averageLabel: `CPS Average (${rep.college_Enrollment_CPS_Pct_Year_2})`,
                    averagePercent: rep.college_Enrollment_CPS_Pct_Year_2
                },
            ]} />

        }
        <div className="report-block awards-block">
            {rep.excelerate_Award_Gold_Year && <div className="award row">
                <div className="info col-9 col-md-10">
                    <h4>{(rep.excelerate_Award_Gold_Year ? rep.excelerate_Award_Gold_Year : 'Incomplete Data')}</h4>
                    <h3>Early Education Award</h3>
                    <p>The Gold Circle of Quality gives statewide recognition to early learning programs that meet or go beyond the highest ExceleRate Illinois<sup>TM</sup> Quality Standards. To learn more, visit: <a href="https://www.excelerateillinois.com/" target="_blank">ExcelerateIllinois.com</a></p>
                </div>
                <div className="image col-3 col-md-2">
                    <img src={`/globalassets/cps-global-media/school-profiles/photos/early-education-logo.jpg`} alt="excelerate" />
                </div>
            </div>
            }
            <div className="award row">
                <div className="info col-9 col-md-10">
                    <h4>{(rep.creative_School_Certification ? rep.creative_School_Certification : 'Incomplete Data')}</h4>
                    <h3>Creative Schools</h3>
                    <p dangerouslySetInnerHTML={{ __html: rep.creative_School_Certification_Description }}></p>
                </div>
                <div className="image col-3 col-md-2">
                    <img src={imgCreativeSchoolCertification} alt="healthy" />
                </div>
            </div>
        </div>
        {es &&
            <div className="stats-section">
                <div className="row">
                    <div className="rated-header col">
                        <div className={`rating-badge ${rep.student_Growth_Rating.toLowerCase().replace(/\s/g, "-")}`}>{rep.student_Growth_Rating}</div>
                        <h2>Student Growth by Grade Level</h2>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-6 rated-stats">
                        <StatChart header="Reading" stats={nweaReading} />
                    </div>
                    <div className="col-sm-6 rated-stats">
                        <StatChart header="Math" stats={nweaMath} />
                    </div>
                </div>

                <div className="row">
                    <div className="rated-header col">
                        <div className={`rating-badge ${rep.student_Attainment_Rating.toLowerCase().replace(/\s/g, "-")}`}>{rep.student_Attainment_Rating}</div>
                        <h2>Student Attainment by Grade Level</h2>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6 rated-stats">
                        <StatChart header="Reading" stats={attainmentReading} />
                    </div>
                    <div className="col-sm-6 rated-stats">
                        <StatChart header="Math" stats={attainmentMath} />
                    </div>
                </div>
            </div>
        }
        {hs && <>
            <div className="stats-section">
                <div className="">
                    <div className="rated-header col">
                        <div className={`rating-badge ${rep.student_Growth_Rating.toLowerCase().replace(/\s/g, "-")}`}>{rep.student_Growth_Rating}</div>
                        <h2>Student Growth by Grade Level</h2>
                        <p>
                            Student Growth measures the change in standardized test scores between two points in time. Cohort growth measures compare this school's growth to other CPS schools' growth. Annual growth measures compare this school's growth to the growth of schools nationwide. To learn more about the PSAT/SAT assessment series, visit <a href="https://collegereadiness.collegeboard.org">https://collegereadiness.collegeboard.org</a>.
                        </p>
                    </div>
                </div>
                <div className="">
                    <StatChart header="Student Growth (Percentile)" stats={hsGrowth} />
                </div>
            </div>
            <div className="stats-section">
                <div className="">
                    <div className="rated-header col">
                        <div className={`rating-badge ${rep.student_Attainment_Rating.toLowerCase().replace(/\s/g, "-")}`}>{rep.student_Growth_Rating}</div>
                        <h2>Student Attainment by Grade Level</h2>
                        <p>
                            Student Attainment measures how well the school performed on the PSAT or SAT standardized test at a single point in time. This school's score is based on attainment relative to the combined College Readiness Benchmark score, set by College Board. A score of 50% means half of this school's students met or exceeded the combined benchmark scores for their grade-level. The charts below show the Student Attainment by grade level. To learn more, visit <a href="https://collegereadiness.collegeboard.org">https://collegereadiness.collegeboard.org</a>.
                        </p>
                    </div>
                </div>
                <div className="">
                    <StatChart header="Student Attainment (Percent Meeting College Readiness Benchmarks)" stats={hsAttainment} />
                </div>
                <br />
            </div>
            <br />
        </>
        }
        {hs && <>
            <h2>Progress Toward Graduation and College</h2>
            <div>
                <StatChart header="Freshman on Track" stats={[
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.freshmen_On_Track_School_Pct_Year_2)}`,
                        percent: rep.freshmen_On_Track_School_Pct_Year_2,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.freshmen_On_Track_CPS_Pct_Year_2)}`,
                        percent: rep.freshmen_On_Track_CPS_Pct_Year_2,
                    },
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.freshmen_On_Track_School_Pct_Year_1)}`,
                        percent: rep.freshmen_On_Track_School_Pct_Year_1,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.freshmen_On_Track_CPS_Pct_Year_1)}`,
                        percent: rep.freshmen_On_Track_CPS_Pct_Year_1,
                    },
                ]} />

                <br />

                <StatChart header="4 Year Graduation" stats={[
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.graduation_4_Year_School_Pct_Year_2)}`,
                        percent: rep.graduation_4_Year_School_Pct_Year_2,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.graduation_4_Year_CPS_Pct_Year_2)}`,
                        percent: rep.graduation_4_Year_CPS_Pct_Year_2,
                    },
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.graduation_4_Year_School_Pct_Year_1)}`,
                        percent: rep.graduation_4_Year_School_Pct_Year_1,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.graduation_4_Year_CPS_Pct_Year_1)}`,
                        percent: rep.graduation_4_Year_CPS_Pct_Year_1,
                    },
                ]} />

                <br />

                <StatChart header="5 Year Graduation" stats={[
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.graduation_5_Year_School_Pct_Year_2)}`,
                        percent: rep.graduation_5_Year_School_Pct_Year_2,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.graduation_5_Year_CPS_Pct_Year_2)}`,
                        percent: rep.graduation_5_Year_CPS_Pct_Year_2,
                    },
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.graduation_5_Year_School_Pct_Year_1)}`,
                        percent: rep.graduation_5_Year_School_Pct_Year_1,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.graduation_5_Year_CPS_Pct_Year_1)}`,
                        percent: rep.graduation_5_Year_CPS_Pct_Year_1,
                    },
                ]} />

                <br />


                <StatChart header="College Enrollment" stats={[
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.college_Enrollment_School_Pct_Year_2)}`,
                        percent: rep.college_Enrollment_School_Pct_Year_2,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.college_Enrollment_CPS_Pct_Year_2)}`,
                        percent: rep.college_Enrollment_CPS_Pct_Year_2,
                    },
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.college_Enrollment_School_Pct_Year_1)}`,
                        percent: rep.college_Enrollment_School_Pct_Year_1,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.college_Enrollment_CPS_Pct_Year_1)}`,
                        percent: rep.college_Enrollment_CPS_Pct_Year_1,
                    },
                ]} />

                <br />
                <StatChart header="College Persistence" stats={[
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.college_Persistence_School_Pct_Year_2)}`,
                        percent: rep.college_Persistence_School_Pct_Year_2,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_2})`,
                        label: `${percentLabel(rep.college_Persistence_CPS_Pct_Year_2)}`,
                        percent: rep.college_Persistence_CPS_Pct_Year_2,
                    },
                    {
                        title: `School (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.college_Persistence_School_Pct_Year_1)}`,
                        percent: rep.college_Persistence_School_Pct_Year_1,
                    },
                    {
                        title: `CPS (${rep.progress_Toward_Graduation_Year_1})`,
                        label: `${percentLabel(rep.college_Persistence_CPS_Pct_Year_1)}`,
                        percent: rep.college_Persistence_CPS_Pct_Year_1,
                    },
                ]} />
            </div>
            <br />
        </>}
        <div className="row">
            <div className="rated-header col">
                <div className={`rating-badge ${rep.culture_Climate_Rating.toLowerCase().replace(/\s/g, "-")}`}>{rep.culture_Climate_Rating}</div>
                <h2>School Culture and Climate Details</h2>
            </div>
        </div>
        <div className="table-scrolling-wrapper">
            <table className="report-table single header">
                <thead>
                    <tr>
                        <th colSpan={2}>
                            <strong>The 5Essentials from the My Voice, My School Survey:</strong> 5Essentials is based on the My Voice, My School 5Essentials survey of teachers and 6th-12th grade students. To see the complete report for your school, visit <a href="https://www.5-essentials.org/">https://www.5-essentials.org/</a>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>Involved Families: </strong>Does the school partner with families and communities?</td>
                        <td><div className={`rating-badge ${rep.school_Survey_Involved_Families.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Involved_Families}</div></td>
                    </tr>
                    <tr>
                        <td><strong>Supportive Environment: </strong>Is the school safe and supportive with high expectations? </td>
                        <td><div className={`rating-badge ${rep.school_Survey_Supportive_Environment.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Supportive_Environment}</div></td>
                    </tr>
                    <tr>
                        <td><strong>Ambitious Instruction: </strong>Is the instruction focused, challenging and engaging? </td>
                        <td><div className={`rating-badge ${rep.school_Survey_Ambitious_Instruction.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Ambitious_Instruction}</div></td>
                    </tr>
                    <tr>
                        <td><strong>Effective Leaders: </strong>Does leadership focus on results and school improvement?</td>
                        <td><div className={`rating-badge ${rep.school_Survey_Effective_Leaders.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Effective_Leaders}</div></td>
                    </tr>
                    <tr>
                        <td><strong>Collaborative Teachers: </strong>Do teachers work well together and strive for excellence?</td>
                        <td><div className={`rating-badge ${rep.school_Survey_Collaborative_Teachers.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Collaborative_Teachers}</div></td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div className="table-scrolling-wrapper">
            <table className="report-table single header">
                <thead>
                    <tr>
                        <th colSpan={2}>
                            Parent Survey Results: Parent Survey Results are based on questions from the My Voice, My School 5Essentials survey. To learn more visit <a href="http://www.cps.edu/parentsurvey">http://www.cps.edu/parentsurvey</a>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>School Community: </strong>Do parents feel welcome and included in the school?</td>
                        <td><div className={`rating-badge ${rep.school_Survey_School_Community.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_School_Community}</div></td>
                    </tr>
                    <tr>
                        <td><strong>Parent-Teacher Partnership: </strong> Do parents feel teachers partner with them to support student success?</td>
                        <td><div className={`rating-badge ${rep.school_Survey_Parent_Teacher_Partnership.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Parent_Teacher_Partnership}</div></td>
                    </tr>
                    <tr>
                        <td><strong>Quality of Facilities: </strong>Do parents feel satisfied with the physical environment of their child's school?</td>
                        <td><div className={`rating-badge ${rep.school_Survey_Quality_Of_Facilities.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Quality_Of_Facilities}</div></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div className="table-scrolling-wrapper">

            <table className="report-table single header">
                <thead>
                    <tr>
                        <th colSpan={2}>
                            <strong>Safety: </strong> Student views of safety from the My Voice My School survey. This measure is not a part of the 5 Essentials but provides additional information on the culture and climate of the school from the perspective of students and teachers.
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>Safety:&nbsp; </strong>Do students feel safe in and around the school bulding and traveling to and from  school?</td>
                        <td><div className={`rating-badge ${rep.school_Survey_Safety.toLowerCase().replace(/\s/g, "-")}`}> {rep.school_Survey_Safety}</div></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div className="table-scrolling-wrapper">

            <table className="report-table">
                <thead>
                    <tr>
                        <th><strong>Behavior and Discipline:</strong> How the school addresses student discipline. To learn more about disciplinary practices at this school and at the district level, access the 'Suspensions and Expulsions' report at <a href="https://www.cps.edu/about/district-data/metrics/">https://www.cps.edu/about/district-data/metrics/</a>.</th>
                        <th>{rep.behavior_Discipline_Year_1}</th>
                        <th>{rep.behavior_Discipline_Year_2}</th>
                        <th>District Average</th>
                    </tr>
                </thead>
                <tbody>
                    {body(behaviorData)}
                </tbody>
            </table>
        </div>
        <div className="table-scrolling-wrapper">

            <table className="report-table">
                <thead>
                    <tr>
                        <th>Other Important Metrics</th>
                        <th>{rep.other_Metrics_Year_1}</th>
                        <th>{rep.other_Metrics_Year_2}</th>
                        <th>District Average</th>
                    </tr>
                </thead>
                <tbody>
                    {body(otherData)}
                </tbody>
            </table>
        </div>
        <div className="table-scrolling-wrapper">
            <table className="report-table">
                <thead>
                    <tr>
                        <th>Survey Response Rate</th>
                        <th>Response Rate</th>
                        <th>CPS Average</th>
                    </tr>
                </thead>
                <tbody>
                    {body(surveyData)}
                </tbody>
            </table>
        </div>
    </div>;
}

export default ReportsTab;